import React, {
    useCallback,
    useEffect,
    useState
} from "react";


const AdminCoins = () => {

    // =========================================================
    // STATE
    // =========================================================

    const [stats, setStats] =
        useState({
            currentCoins: 0,
            totalPurchased: 0,
            totalRefunded: 0,
            netPurchased: 0,
            purchaseTransactions: 0,
            refundTransactions: 0,

            totalCreatorEarnings: 0,
            totalCreatorUsd: 0,

            availableCreatorEarnings: 0,
            availableCreatorUsd: 0
        });


    const [transactions, setTransactions] =
        useState([]);


    const [payouts, setPayouts] =
        useState([]);


    const [loading, setLoading] =
        useState(true);


    const [error, setError] =
        useState("");


    const [payoutActionMessage, setPayoutActionMessage] =
        useState("");


    const [payoutActionLoadingId, setPayoutActionLoadingId] =
        useState(null);


    // =========================================================
    // PAY MODAL
    // =========================================================

    const [showPayModal, setShowPayModal] =
        useState(false);


    const [showRejectModal, setShowRejectModal] =
        useState(false);


    const [selectedPayout, setSelectedPayout] =
        useState(null);


    const [paymentReference, setPaymentReference] =
        useState("");


    // =========================================================
    // AUTH
    // =========================================================

    const getAuthHeader = () => {

        const token =
            localStorage.getItem(
                "authToken"
            );


        if (!token) {
            return "";
        }


        return token.startsWith(
            "Bearer "
        )
            ? token
            : `Bearer ${token}`;
    };


    // =========================================================
    // LOAD DATA
    // =========================================================

    const loadCoinsData =
        useCallback(
            async () => {

                try {

                    setLoading(true);

                    setError("");


                    const authHeader =
                        getAuthHeader();


                    const [
                        statsResponse,
                        transactionsResponse,
                        payoutsResponse
                    ] =
                        await Promise.all([

                            fetch(
                                `${process.env.REACT_APP_API_URL}/api/admin/coins/stats`,
                                {
                                    headers: {
                                        Authorization:
                                            authHeader
                                    }
                                }
                            ),


                            fetch(
                                `${process.env.REACT_APP_API_URL}/api/admin/coins/transactions?page=0&size=50`,
                                {
                                    headers: {
                                        Authorization:
                                            authHeader
                                    }
                                }
                            ),


                            fetch(
                                `${process.env.REACT_APP_API_URL}/api/admin/payouts?page=0&size=50`,
                                {
                                    headers: {
                                        Authorization:
                                            authHeader
                                    }
                                }
                            )
                        ]);


                    const statsData =
                        await statsResponse
                            .json()
                            .catch(() => null);


                    const transactionData =
                        await transactionsResponse
                            .json()
                            .catch(() => null);


                    const payoutsData =
                        await payoutsResponse
                            .json()
                            .catch(() => null);


                    if (!statsResponse.ok) {

                        throw new Error(
                            statsData?.message ||
                            "Unable to load coin statistics."
                        );
                    }


                    if (!transactionsResponse.ok) {

                        throw new Error(
                            transactionData?.message ||
                            "Unable to load coin transactions."
                        );
                    }


                    if (!payoutsResponse.ok) {

                        throw new Error(
                            payoutsData?.message ||
                            "Unable to load creator payouts."
                        );
                    }


                    // =================================================
                    // STATS
                    // =================================================

                    setStats({

                        currentCoins:
                            Number(
                                statsData?.currentCoins ||
                                0
                            ),

                        totalPurchased:
                            Number(
                                statsData?.totalPurchased ||
                                0
                            ),

                        totalRefunded:
                            Number(
                                statsData?.totalRefunded ||
                                0
                            ),

                        netPurchased:
                            Number(
                                statsData?.netPurchased ||
                                0
                            ),

                        purchaseTransactions:
                            Number(
                                statsData?.purchaseTransactions ||
                                0
                            ),

                        refundTransactions:
                            Number(
                                statsData?.refundTransactions ||
                                0
                            ),

                        totalCreatorEarnings:
                            Number(
                                statsData?.totalCreatorEarnings ||
                                0
                            ),

                        totalCreatorUsd:
                            Number(
                                statsData?.totalCreatorUsd ||
                                0
                            ),

                        availableCreatorEarnings:
                            Number(
                                statsData?.availableCreatorEarnings ||
                                0
                            ),

                        availableCreatorUsd:
                            Number(
                                statsData?.availableCreatorUsd ||
                                0
                            )
                    });


                    // =================================================
                    // PURCHASES
                    // =================================================

                    setTransactions(
                        Array.isArray(
                            transactionData?.content
                        )
                            ? transactionData.content
                            : []
                    );


                    // =================================================
                    // PAYOUTS
                    // =================================================

                    setPayouts(
                        Array.isArray(
                            payoutsData?.content
                        )
                            ? payoutsData.content
                            : []
                    );


                } catch (error) {

                    console.error(
                        "Admin coins error:",
                        error
                    );


                    setError(
                        error?.message ||
                        "Unable to load Coins administration."
                    );


                } finally {

                    setLoading(false);
                }
            },
            []
        );


    // =========================================================
    // INITIAL LOAD
    // =========================================================

    useEffect(() => {

        loadCoinsData();

    }, [
        loadCoinsData
    ]);


    // =========================================================
    // OPEN PAY MODAL
    // =========================================================

    const openPayModal =
        (payout) => {

            setSelectedPayout(
                payout
            );

            setPaymentReference(
                ""
            );

            setError("");

            setShowPayModal(
                true
            );
        };


    // =========================================================
    // CLOSE PAY MODAL
    // =========================================================

    const closePayModal = () => {

        if (payoutActionLoadingId) {
            return;
        }

        setShowPayModal(
            false
        );

        setSelectedPayout(
            null
        );

        setPaymentReference(
            ""
        );
    };


    // =========================================================
    // PAY CREATOR
    // =========================================================

    const payPayout =
        async () => {

            if (
                !selectedPayout?.id ||
                !paymentReference.trim()
            ) {
                return;
            }


            try {

                setPayoutActionLoadingId(
                    selectedPayout.id
                );

                setError("");

                setPayoutActionMessage("");


                const response =
                    await fetch(
                        `${process.env.REACT_APP_API_URL}/api/admin/payouts/${selectedPayout.id}/pay?reference=${encodeURIComponent(
                            paymentReference.trim()
                        )}`,
                        {
                            method: "POST",

                            headers: {
                                Authorization:
                                    getAuthHeader()
                            }
                        }
                    );


                const data =
                    await response
                        .json()
                        .catch(() => null);


                if (!response.ok) {

                    throw new Error(
                        data?.message ||
                        "Unable to mark payout as paid."
                    );
                }


                setPayoutActionMessage(
                    `Payout #${selectedPayout.id} successfully marked as PAID.`
                );


                setShowPayModal(
                    false
                );

                setSelectedPayout(
                    null
                );

                setPaymentReference(
                    ""
                );


                await loadCoinsData();


            } catch (error) {

                console.error(
                    "Pay payout error:",
                    error
                );


                setError(
                    error?.message ||
                    "Unable to mark payout as paid."
                );


            } finally {

                setPayoutActionLoadingId(
                    null
                );
            }
        };


    // =========================================================
    // OPEN REJECT MODAL
    // =========================================================

    const openRejectModal =
        (payout) => {

            setSelectedPayout(
                payout
            );

            setError("");

            setShowRejectModal(
                true
            );
        };


    // =========================================================
    // CLOSE REJECT MODAL
    // =========================================================

    const closeRejectModal = () => {

        if (payoutActionLoadingId) {
            return;
        }

        setShowRejectModal(
            false
        );

        setSelectedPayout(
            null
        );
    };


    // =========================================================
    // REJECT PAYOUT
    // =========================================================

    const rejectPayout =
        async () => {

            if (!selectedPayout?.id) {
                return;
            }


            try {

                setPayoutActionLoadingId(
                    selectedPayout.id
                );

                setError("");

                setPayoutActionMessage("");


                const response =
                    await fetch(
                        `${process.env.REACT_APP_API_URL}/api/admin/payouts/${selectedPayout.id}/reject`,
                        {
                            method: "POST",

                            headers: {
                                Authorization:
                                    getAuthHeader()
                            }
                        }
                    );


                const data =
                    await response
                        .json()
                        .catch(() => null);


                if (!response.ok) {

                    throw new Error(
                        data?.message ||
                        "Unable to reject payout."
                    );
                }


                setPayoutActionMessage(
                    `Payout #${selectedPayout.id} has been rejected.`
                );


                setShowRejectModal(
                    false
                );

                setSelectedPayout(
                    null
                );


                await loadCoinsData();


            } catch (error) {

                console.error(
                    "Reject payout error:",
                    error
                );


                setError(
                    error?.message ||
                    "Unable to reject payout."
                );


            } finally {

                setPayoutActionLoadingId(
                    null
                );
            }
        };


    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {

        return (

            <div
                style={{
                    height:
                        "calc(100vh - 70px)",

                    background:
                        "#f6f7fb",

                    display:
                        "flex",

                    alignItems:
                        "center",

                    justifyContent:
                        "center"
                }}
            >

                <div
                    style={{
                        color:
                            "#71717a",

                        fontWeight:
                            "700"
                    }}
                >
                    Loading...
                </div>

            </div>
        );
    }


    // =========================================================
    // PAGE
    // =========================================================

    return (

        <div
            style={{
                height:
                    "calc(100vh - 70px)",

                width:
                    "100%",

                background:
                    "#f6f7fb",

                padding:
                    "32px",

                boxSizing:
                    "border-box",

                overflowY:
                    "auto",

                overflowX:
                    "hidden",

                WebkitOverflowScrolling:
                    "touch"
            }}
        >

            <div
                style={{
                    maxWidth:
                        "1350px",

                    margin:
                        "0 auto"
                }}
            >


                {/* =====================================================
                    HEADER
                ===================================================== */}

                <div
                    style={{
                        marginBottom:
                            "30px"
                    }}
                >

                    <div
                        style={{
                            display:
                                "inline-flex",

                            background:
                                "#fff7ed",

                            color:
                                "#d97706",

                            padding:
                                "7px 13px",

                            borderRadius:
                                "999px",

                            fontSize:
                                "12px",

                            fontWeight:
                                "800",

                            marginBottom:
                                "10px"
                        }}
                    >
                        COINS
                    </div>


                    <h1
                        style={{
                            margin: 0,

                            fontSize:
                                "32px",

                            color:
                                "#18181b"
                        }}
                    >
                        Coins Administration
                    </h1>


                    <p
                        style={{
                            color:
                                "#71717a",

                            marginTop:
                                "8px"
                        }}
                    >
                        Monitor coins, purchases,
                        creator earnings and payouts.
                    </p>

                </div>


                {/* =====================================================
                    SUCCESS MESSAGE
                ===================================================== */}

                {payoutActionMessage && (

                    <div
                        style={{
                            marginBottom:
                                "20px",

                            background:
                                "#ecfdf5",

                            border:
                                "1px solid #bbf7d0",

                            color:
                                "#166534",

                            padding:
                                "14px 16px",

                            borderRadius:
                                "12px",

                            fontWeight:
                                "700"
                        }}
                    >
                        {payoutActionMessage}
                    </div>

                )}


                {/* =====================================================
                    ERROR MESSAGE
                ===================================================== */}

                {error && (

                    <div
                        style={{
                            background:
                                "#fff",

                            border:
                                "1px solid #fecaca",

                            color:
                                "#b91c1c",

                            padding:
                                "15px",

                            borderRadius:
                                "14px",

                            marginBottom:
                                "20px"
                        }}
                    >
                        {error}
                    </div>

                )}


                {/* =====================================================
                    STAT CARDS
                ===================================================== */}

                <div
                    style={{
                        display:
                            "grid",

                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(200px, 1fr))",

                        gap:
                            "16px"
                    }}
                >

                    <CoinStatCard
                        title="Current Coins"
                        value={
                            stats.currentCoins
                        }
                    />


                    <CoinStatCard
                        title="Total Purchased"
                        value={
                            stats.totalPurchased
                        }
                    />


                    <CoinStatCard
                        title="Total Refunded"
                        value={
                            stats.totalRefunded
                        }
                    />


                    <CoinStatCard
                        title="Creator Earnings"
                        value={
                            `$${stats.totalCreatorUsd.toFixed(
                                2
                            )}`
                        }
                        subtitle={
                            `${stats.totalCreatorEarnings.toFixed(
                                2
                            )} earning coins`
                        }
                    />


                    <CoinStatCard
                        title="Available to Creators"
                        value={
                            `$${stats.availableCreatorUsd.toFixed(
                                2
                            )}`
                        }
                        subtitle={
                            `${stats.availableCreatorEarnings.toFixed(
                                2
                            )} earning coins`
                        }
                    />


                    <CoinStatCard
                        title="Net Purchased"
                        value={
                            stats.netPurchased
                        }
                    />


                    <CoinStatCard
                        title="Purchase Transactions"
                        value={
                            stats.purchaseTransactions
                        }
                    />


                    <CoinStatCard
                        title="Refund Transactions"
                        value={
                            stats.refundTransactions
                        }
                    />

                </div>


                {/* =====================================================
                    COIN PURCHASE HISTORY
                ===================================================== */}

                <div
                    style={{
                        marginTop:
                            "35px",

                        background:
                            "#fff",

                        borderRadius:
                            "20px",

                        border:
                            "1px solid #ececf1",

                        boxShadow:
                            "0 8px 28px rgba(15,23,42,.05)",

                        overflow:
                            "hidden"
                    }}
                >

                    <SectionHeader
                        title="Recent Coin Purchases"
                        subtitle="Coin purchase history"
                        onRefresh={
                            loadCoinsData
                        }
                    />


                    <div
                        style={{
                            overflowX:
                                "auto"
                        }}
                    >

                        <table
                            style={{
                                width:
                                    "100%",

                                borderCollapse:
                                    "collapse",

                                minWidth:
                                    "750px"
                            }}
                        >

                            <thead>

                                <tr
                                    style={{
                                        background:
                                            "#fafafa"
                                    }}
                                >

                                    <TableHeader>
                                        User ID
                                    </TableHeader>

                                    <TableHeader>
                                        Coins
                                    </TableHeader>

                                    <TableHeader>
                                        Provider
                                    </TableHeader>

                                    <TableHeader>
                                        Date
                                    </TableHeader>

                                    <TableHeader>
                                        Status
                                    </TableHeader>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    transactions.length ===
                                    0 ? (

                                        <tr>

                                            <td
                                                colSpan={
                                                    5
                                                }
                                                style={{
                                                    padding:
                                                        "35px",

                                                    textAlign:
                                                        "center",

                                                    color:
                                                        "#71717a"
                                                }}
                                            >
                                                No coin purchases found.
                                            </td>

                                        </tr>

                                    ) : (

                                        transactions.map(
                                            transaction => (

                                                <tr
                                                    key={
                                                        transaction.id
                                                    }
                                                    style={{
                                                        borderTop:
                                                            "1px solid #f1f1f4"
                                                    }}
                                                >

                                                    <TableCell>
                                                        {
                                                            transaction.userId
                                                        }
                                                    </TableCell>


                                                    <TableCell>

                                                        <strong>
                                                            {
                                                                transaction.amount
                                                            }
                                                        </strong>

                                                    </TableCell>


                                                    <TableCell>
                                                        {
                                                            transaction.paymentProvider ||
                                                            "UNKNOWN"
                                                        }
                                                    </TableCell>


                                                    <TableCell>
                                                        {
                                                            formatDate(
                                                                transaction.createdAt
                                                            )
                                                        }
                                                    </TableCell>


                                                    <TableCell>

                                                        <PurchaseStatusBadge
                                                            refunded={
                                                                Boolean(
                                                                    transaction.refunded
                                                                )
                                                            }
                                                        />

                                                    </TableCell>

                                                </tr>

                                            )
                                        )

                                    )
                                }

                            </tbody>

                        </table>

                    </div>

                </div>


                {/* =====================================================
                    CREATOR PAYOUT REQUESTS
                ===================================================== */}

                <div
                    style={{
                        marginTop:
                            "35px",

                        marginBottom:
                            "40px",

                        background:
                            "#fff",

                        borderRadius:
                            "20px",

                        border:
                            "1px solid #ececf1",

                        boxShadow:
                            "0 8px 28px rgba(15,23,42,.05)",

                        overflow:
                            "hidden"
                    }}
                >

                    <SectionHeader
                        title="Creator Payout Requests"
                        subtitle="Review creator withdrawal requests"
                        onRefresh={
                            loadCoinsData
                        }
                    />


                    <div
                        style={{
                            overflowX:
                                "auto"
                        }}
                    >

                        <table
                            style={{
                                width:
                                    "100%",

                                borderCollapse:
                                    "collapse",

                                minWidth:
                                    "1100px"
                            }}
                        >

                            <thead>

                                <tr
                                    style={{
                                        background:
                                            "#fafafa"
                                    }}
                                >

                                    <TableHeader>
                                        Creator ID
                                    </TableHeader>

                                    <TableHeader>
                                        Amount
                                    </TableHeader>

                                    <TableHeader>
                                        Method
                                    </TableHeader>

                                    <TableHeader>
                                        Destination
                                    </TableHeader>

                                    <TableHeader>
                                        Requested
                                    </TableHeader>

                                    <TableHeader>
                                        Status
                                    </TableHeader>

                                    <TableHeader>
                                        Action
                                    </TableHeader>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    payouts.length ===
                                    0 ? (

                                        <tr>

                                            <td
                                                colSpan={
                                                    7
                                                }
                                                style={{
                                                    padding:
                                                        "35px",

                                                    textAlign:
                                                        "center",

                                                    color:
                                                        "#71717a"
                                                }}
                                            >
                                                No creator payout requests found.
                                            </td>

                                        </tr>

                                    ) : (

                                        payouts.map(
                                            payout => (

                                                <tr
                                                    key={
                                                        payout.id
                                                    }
                                                    style={{
                                                        borderTop:
                                                            "1px solid #f1f1f4"
                                                    }}
                                                >

                                                    <TableCell>
                                                        {
                                                            payout.userId
                                                        }
                                                    </TableCell>


                                                    <TableCell>

                                                        <strong>
                                                            $
                                                            {
                                                                Number(
                                                                    payout.amountUsd ||
                                                                    0
                                                                ).toFixed(
                                                                    2
                                                                )
                                                            }
                                                        </strong>

                                                    </TableCell>


                                                    <TableCell>

                                                        <PayoutMethodBadge
                                                            method={
                                                                payout.method
                                                            }
                                                        />

                                                    </TableCell>


                                                    <TableCell>

                                                        {
                                                            payout.payoutDestination ||
                                                            "-"
                                                        }

                                                    </TableCell>


                                                    <TableCell>

                                                        {
                                                            formatDate(
                                                                payout.requestedAt
                                                            )
                                                        }

                                                    </TableCell>


                                                    <TableCell>

                                                        <PayoutStatusBadge
                                                            status={
                                                                payout.status
                                                            }
                                                        />

                                                    </TableCell>


                                                    <TableCell>

                                                        {
                                                            payout.status ===
                                                            "PENDING" ? (

                                                                <div
                                                                    style={{
                                                                        display:
                                                                            "flex",

                                                                        gap:
                                                                            "8px",

                                                                        flexWrap:
                                                                            "wrap"
                                                                    }}
                                                                >

                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            openPayModal(
                                                                                payout
                                                                            )
                                                                        }
                                                                        style={{
                                                                            border:
                                                                                "none",

                                                                            background:
                                                                                "#16a34a",

                                                                            color:
                                                                                "#fff",

                                                                            padding:
                                                                                "8px 13px",

                                                                            borderRadius:
                                                                                "8px",

                                                                            cursor:
                                                                                "pointer",

                                                                            fontWeight:
                                                                                "700"
                                                                        }}
                                                                    >
                                                                        Pay
                                                                    </button>


                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            openRejectModal(
                                                                                payout
                                                                            )
                                                                        }
                                                                        style={{
                                                                            border:
                                                                                "none",

                                                                            background:
                                                                                "#ef4444",

                                                                            color:
                                                                                "#fff",

                                                                            padding:
                                                                                "8px 13px",

                                                                            borderRadius:
                                                                                "8px",

                                                                            cursor:
                                                                                "pointer",

                                                                            fontWeight:
                                                                                "700"
                                                                        }}
                                                                    >
                                                                        Reject
                                                                    </button>

                                                                </div>

                                                            ) : (

                                                                <span
                                                                    style={{
                                                                        color:
                                                                            "#9ca3af",

                                                                        fontWeight:
                                                                            "700"
                                                                    }}
                                                                >
                                                                    Completed
                                                                </span>

                                                            )
                                                        }

                                                    </TableCell>

                                                </tr>

                                            )
                                        )

                                    )
                                }

                            </tbody>

                        </table>

                    </div>

                </div>


            </div>


            {/* =====================================================
                PAY MODAL
            ===================================================== */}

            {
                showPayModal &&
                selectedPayout && (

                    <ModalOverlay>

                        <ModalCard>

                            <h2
                                style={{
                                    margin:
                                        "0 0 8px",

                                    color:
                                        "#18181b"
                                }}
                            >
                                Pay Creator
                            </h2>


                            <p
                                style={{
                                    margin:
                                        "0 0 20px",

                                    color:
                                        "#71717a"
                                }}
                            >
                                Confirm the payout and enter
                                the payment reference.
                            </p>


                            <PayoutSummary
                                payout={
                                    selectedPayout
                                }
                            />


                            <label
                                style={{
                                    display:
                                        "block",

                                    fontSize:
                                        "13px",

                                    fontWeight:
                                        "700",

                                    marginBottom:
                                        "7px",

                                    color:
                                        "#27272a"
                                }}
                            >
                                Payment reference
                            </label>


                            <input
                                type="text"
                                value={
                                    paymentReference
                                }
                                onChange={e =>
                                    setPaymentReference(
                                        e.target.value
                                    )
                                }
                                placeholder="Example: PAYPAL-ABC123"
                                style={{
                                    width:
                                        "100%",

                                    padding:
                                        "13px 14px",

                                    border:
                                        "1px solid #d4d4d8",

                                    borderRadius:
                                        "12px",

                                    outline:
                                        "none",

                                    boxSizing:
                                        "border-box",

                                    fontSize:
                                        "14px"
                                }}
                            />


                            <div
                                style={{
                                    marginTop:
                                        "22px",

                                    display:
                                        "flex",

                                    justifyContent:
                                        "flex-end",

                                    gap:
                                        "10px"
                                }}
                            >

                                <button
                                    type="button"
                                    disabled={
                                        Boolean(
                                            payoutActionLoadingId
                                        )
                                    }
                                    onClick={
                                        closePayModal
                                    }
                                    style={{
                                        border:
                                            "1px solid #e4e4e7",

                                        background:
                                            "#fff",

                                        padding:
                                            "10px 16px",

                                        borderRadius:
                                            "10px",

                                        cursor:
                                            "pointer",

                                        fontWeight:
                                            "700"
                                    }}
                                >
                                    Cancel
                                </button>


                                <button
                                    type="button"
                                    disabled={
                                        payoutActionLoadingId ===
                                            selectedPayout.id ||
                                        !paymentReference.trim()
                                    }
                                    onClick={
                                        payPayout
                                    }
                                    style={{
                                        border:
                                            "none",

                                        background:
                                            !paymentReference.trim()
                                                ? "#86efac"
                                                : "#16a34a",

                                        color:
                                            "#fff",

                                        padding:
                                            "10px 18px",

                                        borderRadius:
                                            "10px",

                                        cursor:
                                            !paymentReference.trim()
                                                ? "not-allowed"
                                                : "pointer",

                                        fontWeight:
                                            "800"
                                    }}
                                >
                                    {
                                        payoutActionLoadingId ===
                                        selectedPayout.id
                                            ? "Processing..."
                                            : "Confirm Payment"
                                    }
                                </button>

                            </div>

                        </ModalCard>

                    </ModalOverlay>

                )
            }


            {/* =====================================================
                REJECT MODAL
            ===================================================== */}

            {
                showRejectModal &&
                selectedPayout && (

                    <ModalOverlay>

                        <ModalCard>

                            <div
                                style={{
                                    width:
                                        "48px",

                                    height:
                                        "48px",

                                    borderRadius:
                                        "50%",

                                    background:
                                        "#fee2e2",

                                    display:
                                        "flex",

                                    alignItems:
                                        "center",

                                    justifyContent:
                                        "center",

                                    fontSize:
                                        "22px",

                                    marginBottom:
                                        "15px"
                                }}
                            >
                                ⚠️
                            </div>


                            <h2
                                style={{
                                    margin:
                                        "0 0 8px",

                                    color:
                                        "#18181b"
                                }}
                            >
                                Reject payout?
                            </h2>


                            <p
                                style={{
                                    color:
                                        "#71717a",

                                    margin:
                                        "0 0 20px"
                                }}
                            >
                                The reserved creator earnings
                                will be returned to the creator's
                                available balance.
                            </p>


                            <PayoutSummary
                                payout={
                                    selectedPayout
                                }
                            />


                            <div
                                style={{
                                    marginTop:
                                        "22px",

                                    display:
                                        "flex",

                                    justifyContent:
                                        "flex-end",

                                    gap:
                                        "10px"
                                }}
                            >

                                <button
                                    type="button"
                                    disabled={
                                        Boolean(
                                            payoutActionLoadingId
                                        )
                                    }
                                    onClick={
                                        closeRejectModal
                                    }
                                    style={{
                                        border:
                                            "1px solid #e4e4e7",

                                        background:
                                            "#fff",

                                        padding:
                                            "10px 16px",

                                        borderRadius:
                                            "10px",

                                        cursor:
                                            "pointer",

                                        fontWeight:
                                            "700"
                                    }}
                                >
                                    Cancel
                                </button>


                                <button
                                    type="button"
                                    disabled={
                                        payoutActionLoadingId ===
                                        selectedPayout.id
                                    }
                                    onClick={
                                        rejectPayout
                                    }
                                    style={{
                                        border:
                                            "none",

                                        background:
                                            "#ef4444",

                                        color:
                                            "#fff",

                                        padding:
                                            "10px 18px",

                                        borderRadius:
                                            "10px",

                                        cursor:
                                            "pointer",

                                        fontWeight:
                                            "800"
                                    }}
                                >
                                    {
                                        payoutActionLoadingId ===
                                        selectedPayout.id
                                            ? "Rejecting..."
                                            : "Reject Payout"
                                    }
                                </button>

                            </div>

                        </ModalCard>

                    </ModalOverlay>

                )
            }

        </div>
    );
};


// =========================================================
// PAYOUT SUMMARY
// =========================================================

const PayoutSummary = ({
    payout
}) => {

    return (

        <div
            style={{
                background:
                    "#f8fafc",

                border:
                    "1px solid #f1f5f9",

                borderRadius:
                    "14px",

                padding:
                    "15px",

                marginBottom:
                    "18px"
            }}
        >

            <SummaryRow
                label="Creator"
                value={
                    `#${payout?.userId || "-"}`
                }
            />


            <SummaryRow
                label="Amount"
                value={
                    `$${Number(
                        payout?.amountUsd ||
                        0
                    ).toFixed(2)}`
                }
            />


            <SummaryRow
                label="Method"
                value={
                    payout?.method ||
                    "-"
                }
            />


            <SummaryRow
                label="Destination"
                value={
                    payout?.payoutDestination ||
                    "-"
                }
                last
            />

        </div>
    );
};


// =========================================================
// SUMMARY ROW
// =========================================================

const SummaryRow = ({
    label,
    value,
    last
}) => {

    return (

        <div
            style={{
                display:
                    "flex",

                justifyContent:
                    "space-between",

                gap:
                    "15px",

                paddingBottom:
                    last
                        ? "0"
                        : "10px",

                marginBottom:
                    last
                        ? "0"
                        : "10px",

                borderBottom:
                    last
                        ? "none"
                        : "1px solid #e5e7eb",

                fontSize:
                    "13px"
            }}
        >

            <span
                style={{
                    color:
                        "#71717a"
                }}
            >
                {label}
            </span>


            <strong
                style={{
                    color:
                        "#18181b",

                    textAlign:
                        "right",

                    wordBreak:
                        "break-word"
                }}
            >
                {value}
            </strong>

        </div>
    );
};


// =========================================================
// MODAL
// =========================================================

const ModalOverlay = ({
    children
}) => {

    return (

        <div
            style={{
                position:
                    "fixed",

                inset:
                    0,

                background:
                    "rgba(15,23,42,.55)",

                display:
                    "flex",

                alignItems:
                    "center",

                justifyContent:
                    "center",

                zIndex:
                    9999,

                padding:
                    "20px"
            }}
        >
            {children}
        </div>
    );
};


const ModalCard = ({
    children
}) => {

    return (

        <div
            style={{
                width:
                    "100%",

                maxWidth:
                    "470px",

                background:
                    "#fff",

                borderRadius:
                    "20px",

                padding:
                    "24px",

                boxSizing:
                    "border-box",

                boxShadow:
                    "0 24px 70px rgba(0,0,0,.25)"
            }}
        >
            {children}
        </div>
    );
};


// =========================================================
// SECTION HEADER
// =========================================================

const SectionHeader = ({
    title,
    subtitle,
    onRefresh
}) => {

    return (

        <div
            style={{
                padding:
                    "22px",

                borderBottom:
                    "1px solid #f1f1f4",

                display:
                    "flex",

                alignItems:
                    "center",

                justifyContent:
                    "space-between",

                gap:
                    "15px",

                flexWrap:
                    "wrap"
            }}
        >

            <div>

                <h2
                    style={{
                        margin:
                            0,

                        fontSize:
                            "20px"
                    }}
                >
                    {title}
                </h2>


                <p
                    style={{
                        margin:
                            "5px 0 0",

                        color:
                            "#71717a",

                        fontSize:
                            "13px"
                    }}
                >
                    {subtitle}
                </p>

            </div>


            <button
                type="button"
                onClick={
                    onRefresh
                }
                style={{
                    border:
                        "1px solid #e4e4e7",

                    background:
                        "#fff",

                    padding:
                        "9px 14px",

                    borderRadius:
                        "10px",

                    cursor:
                        "pointer",

                    fontWeight:
                        "700"
                }}
            >
                Refresh
            </button>

        </div>
    );
};


// =========================================================
// STAT CARD
// =========================================================

const CoinStatCard = ({
    title,
    value,
    subtitle
}) => {

    return (

        <div
            style={{
                background:
                    "#fff",

                border:
                    "1px solid #ececf1",

                borderRadius:
                    "18px",

                padding:
                    "20px",

                boxShadow:
                    "0 7px 22px rgba(15,23,42,.04)"
            }}
        >

            <div
                style={{
                    fontSize:
                        "13px",

                    color:
                        "#71717a",

                    fontWeight:
                        "700"
                }}
            >
                {title}
            </div>


            <div
                style={{
                    marginTop:
                        "8px",

                    fontSize:
                        "29px",

                    fontWeight:
                        "850",

                    color:
                        "#18181b"
                }}
            >

                {
                    typeof value ===
                    "number"
                        ? value.toLocaleString()
                        : value
                }

            </div>


            {
                subtitle && (

                    <div
                        style={{
                            marginTop:
                                "7px",

                            color:
                                "#a1a1aa",

                            fontSize:
                                "12px"
                        }}
                    >
                        {subtitle}
                    </div>

                )
            }

        </div>
    );
};


// =========================================================
// PURCHASE STATUS
// =========================================================

const PurchaseStatusBadge = ({
    refunded
}) => {

    return (

        <span
            style={{
                display:
                    "inline-block",

                padding:
                    "5px 10px",

                borderRadius:
                    "999px",

                fontSize:
                    "11px",

                fontWeight:
                    "800",

                background:
                    refunded
                        ? "#fee2e2"
                        : "#dcfce7",

                color:
                    refunded
                        ? "#b91c1c"
                        : "#15803d"
            }}
        >

            {
                refunded
                    ? "REFUNDED"
                    : "PAID"
            }

        </span>
    );
};


// =========================================================
// PAYOUT METHOD
// =========================================================

const PayoutMethodBadge = ({
    method
}) => {

    const normalized =
        String(
            method ||
            "UNKNOWN"
        ).toUpperCase();


    return (

        <span
            style={{
                display:
                    "inline-block",

                padding:
                    "5px 10px",

                borderRadius:
                    "999px",

                fontSize:
                    "11px",

                fontWeight:
                    "800",

                background:
                    "#eef2ff",

                color:
                    "#4338ca"
            }}
        >
            {normalized}
        </span>
    );
};


// =========================================================
// PAYOUT STATUS
// =========================================================

const PayoutStatusBadge = ({
    status
}) => {

    const normalized =
        String(
            status ||
            ""
        ).toUpperCase();


    let background =
        "#fef3c7";

    let color =
        "#b45309";


    if (
        normalized ===
        "PAID"
    ) {

        background =
            "#dcfce7";

        color =
            "#15803d";
    }


    if (
        normalized ===
        "REJECTED"
    ) {

        background =
            "#fee2e2";

        color =
            "#b91c1c";
    }


    if (
        normalized ===
        "FAILED"
    ) {

        background =
            "#fee2e2";

        color =
            "#b91c1c";
    }


    return (

        <span
            style={{
                display:
                    "inline-block",

                padding:
                    "5px 10px",

                borderRadius:
                    "999px",

                fontSize:
                    "11px",

                fontWeight:
                    "800",

                background,

                color
            }}
        >
            {
                normalized ||
                "UNKNOWN"
            }
        </span>
    );
};


// =========================================================
// TABLE HEADER
// =========================================================

const TableHeader = ({
    children
}) => {

    return (

        <th
            style={{
                padding:
                    "14px 18px",

                textAlign:
                    "left",

                color:
                    "#71717a",

                fontSize:
                    "12px",

                fontWeight:
                    "800"
            }}
        >
            {children}
        </th>
    );
};


// =========================================================
// TABLE CELL
// =========================================================

const TableCell = ({
    children
}) => {

    return (

        <td
            style={{
                padding:
                    "16px 18px",

                fontSize:
                    "13px",

                color:
                    "#27272a",

                verticalAlign:
                    "middle"
            }}
        >
            {children}
        </td>
    );
};


// =========================================================
// FORMAT DATE
// =========================================================

const formatDate = (
    value
) => {

    if (!value) {
        return "-";
    }


    const date =
        new Date(
            value
        );


    if (
        Number.isNaN(
            date.getTime()
        )
    ) {

        return value;
    }


    return date.toLocaleString();
};


export default AdminCoins;